import {Point, Graphics, Container, Assets, AnimatedSprite, Text} from 'pixi.js';
import BPromise from 'bluebird';
import {some as _some} from 'lodash/collection';
import {delay as _delay} from 'lodash/function';
import {inRange as _inRange} from 'lodash/number';
import Utils from '../libs/utils';
import Duck from './Duck';
import Dog from './Dog';
import Hud from './Hud';
import {GridOverlay, GRID_H} from './Grid';

const MAX_X = 800;
const MAX_Y = 600;

// The painted grid was for the manual-posting era; the phone draws its own AR
// grid now, so the screen stays clean NES. ?grid=1 brings it back for debugging.
const SHOW_GRID = new URLSearchParams(window.location.search).get('grid') === '1';

const DUCK_POINTS = {
  ORIGIN: new Point(MAX_X / 2, MAX_Y)
};
const DOG_POINTS = {
  DOWN: new Point(MAX_X / 2, MAX_Y),
  UP: new Point(MAX_X / 2, MAX_Y - 230),
  SNIFF_START: new Point(0, MAX_Y - 130),
  SNIFF_END: new Point(MAX_X / 2, MAX_Y - 130)
};
const HUD_LOCATIONS = {
  SCORE: new Point(MAX_X - 10, 10),
  WAVE_STATUS: new Point(MAX_X - 80, MAX_Y - 30), // clear of the br marker QR
  LEVEL_CREATOR_LINK: new Point(MAX_X - 11, MAX_Y - 10),
  FULL_SCREEN_LINK: new Point(MAX_X - 130, MAX_Y - 10),
  PAUSE_LINK: new Point(MAX_X - 318, MAX_Y - 10),
  MUTE_LINK: new Point(MAX_X - 236, MAX_Y - 10),
  GAME_STATUS: new Point(MAX_X / 2, MAX_Y * 0.45),
  REPLAY_BUTTON: new Point(MAX_X / 2, MAX_Y * 0.56),
  BULLET_STATUS: new Point(10, 10),
  DEAD_DUCK_STATUS: new Point(10, MAX_Y * 0.91),
  MISSED_DUCK_STATUS: new Point(10, MAX_Y * 0.95)
};

const FLASH_MS = 60;
const FLASH_SCREEN = new Graphics();
FLASH_SCREEN.rect(0, 0, MAX_X, MAX_Y).fill(0xFFFFFF);
FLASH_SCREEN.position.x = 0;
FLASH_SCREEN.position.y = 0;

class Stage extends Container {

  /**
   * Stage Constructor
   * Container for the game
   * @param opts
   * @param opts.spritesheet - String representing the path to the spritesheet file
   */
  constructor(opts) {
    super();
    this.locked = false;
    this.spritesheet = opts.spritesheet;
    this.ducks = [];
    this.dog = new Dog({
      spritesheet: opts.spritesheet,
      downPoint: DOG_POINTS.DOWN,
      upPoint: DOG_POINTS.UP
    });
    this.dog.visible = false;
    this.flashScreen = FLASH_SCREEN;
    this.flashScreen.visible = false;
    this.hud = new Hud();

    this._setStage();
    this.scaleToWindow();
  }

  static scoreBoxLocation() {
    return HUD_LOCATIONS.SCORE;
  }

  static waveStatusBoxLocation() {
    return HUD_LOCATIONS.WAVE_STATUS;
  }

  static gameStatusBoxLocation() {
    return HUD_LOCATIONS.GAME_STATUS;
  }

  static pauseLinkBoxLocation() {
    return HUD_LOCATIONS.PAUSE_LINK;
  }

  static muteLinkBoxLocation() {
    return HUD_LOCATIONS.MUTE_LINK;
  }

  static fullscreenLinkBoxLocation() {
    return HUD_LOCATIONS.FULL_SCREEN_LINK;
  }

  static levelCreatorLinkBoxLocation() {
    return HUD_LOCATIONS.LEVEL_CREATOR_LINK;
  }

  static replayButtonLocation() {
    return HUD_LOCATIONS.REPLAY_BUTTON;
  }

  static bulletStatusBoxLocation() {
    return HUD_LOCATIONS.BULLET_STATUS;
  }

  static deadDuckStatusBoxLocation() {
    return HUD_LOCATIONS.DEAD_DUCK_STATUS;
  }

  static missedDuckStatusBoxLocation() {
    return HUD_LOCATIONS.MISSED_DUCK_STATUS;
  }

  pause() {
    this.dog.timeline.pause();
    this.ducks.forEach((duck) => {
      duck.timeline.pause();
    });
  }

  resume() {
    this.dog.timeline.play();
    this.ducks.forEach((duck) => {
      duck.timeline.play();
    });
  }

  /**
   * scaleToWindow
   * Helper method that scales the stage container to the window size
   */
  /** Stage units of one letterbox bar (0 on a true-4:3 window). */
  sideBarUnits() {
    const s = this.scale.x;
    return s > 0 ? this.position.x / s : 0; // zero-size window must not spread NaN
  }

  scaleToWindow() {
    // uniform scale + centring: non-4:3 displays (Portal is 3:2) get black
    // letterbox bars instead of a stretched stage
    const s = Math.min(window.innerWidth / MAX_X, window.innerHeight / MAX_Y);
    this.scale.set(s, s);
    this.position.set(
      Math.round((window.innerWidth - MAX_X * s) / 2),
      Math.round((window.innerHeight - MAX_Y * s) / 2));
  }

  /**
   * _setStage
   * Private method that adds all of the main pieces to the scene
   * @returns {Stage}
   * @private
   */
  _setStage() {
    this.sky = new Graphics();
    this.skyColor(0x64b0ff);

    const background = new AnimatedSprite([
      Assets.get(this.spritesheet).textures['scene/back/0.png']
    ]);
    background.position.set(0, 0);

    // the scene continues into the letterbox bars: the same ground strip
    // tiled once per side — the bars show slivers of the neighbouring tile's
    // bushes, so a non-4:3 panel reads as a wider field, not a boxed game
    const groundLeft = new AnimatedSprite([Assets.get(this.spritesheet).textures['scene/back/0.png']]);
    groundLeft.position.set(-MAX_X + 300, 0);
    const groundRight = new AnimatedSprite([Assets.get(this.spritesheet).textures['scene/back/0.png']]);
    groundRight.position.set(MAX_X - 300, 0);

    const tree = new AnimatedSprite([Assets.get(this.spritesheet).textures['scene/tree/0.png']]);
    tree.position.set(100, 237);

    this.grid = new GridOverlay();
    this.grid.visible = SHOW_GRID;
    this.impacts = new Container(); // avatar bursts, above the scene, below the HUD
    this.markersPanel = new Container(); // tiny corner QRs the phone tracks for edges
    this.overlayLayer = new Container(); // celebration / podium, topmost

    // sky + letterbox scenery live in ONE backdrop child, always index 0 —
    // ducks and the retreating dog are inserted at index 1, just above it
    // (they used to go to index 0, which put them UNDER the opaque sky rect)
    this.backdrop = new Container();
    this.backdrop.addChild(this.sky, groundLeft, groundRight);
    this.addChild(this.backdrop);
    this.addChild(tree);
    this.addChild(background);
    this.addChild(this.grid);
    this.addChild(this.dog);
    this.addChild(this.flashScreen);
    this.addChild(this.impacts);
    this.addChild(this.hud);
    this.addChild(this.overlayLayer);
    this.addChild(this.markersPanel); // topmost: the phone must see the corner
                                      // markers through lobby/celebration dims

    return this;
  }

  /** The four static corner-marker QRs (textures keyed tl/tr/br/bl). */
  setCornerMarkers(textures, rects) {
    for (const old of this.markersPanel.removeChildren()) {
      old.destroy({children: true, texture: true, textureSource: true}); // per-round QR textures
    }
    for (const key of Object.keys(rects)) {
      const r = rects[key];
      const texture = textures[key];
      if (!texture) {
        continue;
      }
      this.markersPanel.addChild(new Graphics().rect(r.x - 3, r.y - 3, r.s + 6, r.s + 6).fill(0xf0d9a8));
      const qr = new AnimatedSprite([texture]);
      qr.width = r.s;
      qr.height = r.s;
      qr.position.set(r.x, r.y);
      this.markersPanel.addChild(qr);
    }
  }

  /** Rebuild the grid overlay after the difficulty (grid density) changes. */
  rebuildGrid() {
    const index = this.getChildIndex(this.grid);
    this.removeChild(this.grid).destroy({children: true, texture: true, textureSource: true});
    this.grid = new GridOverlay();
    this.grid.visible = SHOW_GRID;
    this.addChildAt(this.grid, index);
  }

  /** Drop an impact (or notice) display object at a stage-space point. */
  addImpact(displayObject, point) {
    displayObject.position.set(point.x, point.y);
    this.impacts.addChild(displayObject);
    return displayObject;
  }

  clearImpacts() {
    for (const old of this.impacts.removeChildren()) {
      old.destroy({children: true}); // avatar textures are CACHED — keep them
    }
  }

  showOverlay(container) {
    this.clearOverlay();
    this.overlayLayer.addChild(container);
  }

  clearOverlay() {
    for (const old of this.overlayLayer.removeChildren()) {
      old.destroy({children: true}); // avatar textures are CACHED — keep them
    }
  }

  /**
   * preLevelAnimation
   * Helper method that runs the level intro animation with the dog and returns a promise that resolves
   * when it's complete.
   * @returns {Promise}
   */
  preLevelAnimation() {
    return new BPromise((resolve) => {
      this.cleanUpDucks();

      const sniffOpts = {
        startPoint: DOG_POINTS.SNIFF_START,
        endPoint: DOG_POINTS.SNIFF_END
      };

      const findOpts = {
        onComplete: () => {
          this.setChildIndex(this.dog, 1);
          resolve();
        }
      };

      this.dog.sniff(sniffOpts).find(findOpts);
    });
  }

  /**
   * addDucks
   * Helper method that adds ducks to the container and causes them to fly around randomly.
   * @param {Number} numDucks - How many ducks to add to the stage
   * @param {Number} speed - Value from 0 (slow) to 10 (fast) that determines how fast the ducks will fly
   */
  addDucks(numDucks, speed) {
    for (let i = 0; i < numDucks; i++) {
      const duckColor = i % 2 === 0 ? 'red' : 'black';

      // Al was here.
      const newDuck = new Duck({
        spritesheet: this.spritesheet,
        colorProfile: duckColor,
        maxX: MAX_X,
        maxY: GRID_H - 40, // fly the SKY only — the named grid; grass is scenery
        groundY: MAX_Y - 40 // …but a shot duck still falls all the way down
      });
      newDuck.position.set(DUCK_POINTS.ORIGIN.x, DUCK_POINTS.ORIGIN.y);
      this.addChildAt(newDuck, 1); // above the backdrop, behind tree/grass
      newDuck.randomFlight({
        speed
      });

      this.ducks.push(newDuck);
    }
  }

  /**
   * shotsFired
   * Click handler for the stage, scale's the location of the click to ensure coordinate system
   * alignment and then calculates if any of the ducks were hit and should be shot.
   * @param {{x:Number, y:Number}} clickPoint - Point where the container was clicked in real coordinates
   * @param {Number} radius - The "blast radius" of the player's weapon
   * @returns {Number} - The number of ducks hit with the shot
   */
  shotsFired(clickPoint, radius) {
    // flash the screen
    this.flashScreen.visible = true;
    _delay(() => {
      this.flashScreen.visible = false;
    }, FLASH_MS);

    let ducksShot = 0;
    for (let i = 0; i < this.ducks.length; i++) {
      const duck = this.ducks[i];
      if (duck.alive && Utils.pointDistance(duck.position, this.getScaledClickLocation(clickPoint)) < radius) {
        ducksShot++;
        duck.shot();
        duck.timeline.call(() => {
          if (!this.isLocked()) {
            this.dog.retrieve();
          }
        });
      }
    }
    return ducksShot;
  }

  clickedReplay(clickPoint) {
    // this link is in the middle of the page, general radius search is sufficient here
    return Utils.pointDistance(this.getScaledClickLocation(clickPoint), HUD_LOCATIONS.REPLAY_BUTTON) < 200;
  }

  clickedLevelCreatorLink(clickPoint) {
    const scaledClickPoint = this.getScaledClickLocation(clickPoint);

    // with this link we have a very narrow hit box, radius search is not appropriate
    return _inRange(scaledClickPoint.x, HUD_LOCATIONS.LEVEL_CREATOR_LINK.x-110, HUD_LOCATIONS.LEVEL_CREATOR_LINK.x) &&
      _inRange(scaledClickPoint.y, HUD_LOCATIONS.LEVEL_CREATOR_LINK.y-30, HUD_LOCATIONS.LEVEL_CREATOR_LINK.y+10);
  }

  clickedPauseLink(clickPoint) {
    const scaledClickPoint = this.getScaledClickLocation(clickPoint);
    return _inRange(scaledClickPoint.x, HUD_LOCATIONS.PAUSE_LINK.x-110, HUD_LOCATIONS.PAUSE_LINK.x) &&
      _inRange(scaledClickPoint.y, HUD_LOCATIONS.PAUSE_LINK.y-30, HUD_LOCATIONS.PAUSE_LINK.y+10);
  }

  clickedFullscreenLink(clickPoint) {
    const scaledClickPoint = this.getScaledClickLocation(clickPoint);
    return _inRange(scaledClickPoint.x, HUD_LOCATIONS.FULL_SCREEN_LINK.x-110, HUD_LOCATIONS.FULL_SCREEN_LINK.x) &&
      _inRange(scaledClickPoint.y, HUD_LOCATIONS.FULL_SCREEN_LINK.y-30, HUD_LOCATIONS.FULL_SCREEN_LINK.y+10);
  }

  clickedMuteLink(clickPoint) {
    const scaledClickPoint = this.getScaledClickLocation(clickPoint);
    return _inRange(scaledClickPoint.x, HUD_LOCATIONS.MUTE_LINK.x-110, HUD_LOCATIONS.MUTE_LINK.x) &&
      _inRange(scaledClickPoint.y, HUD_LOCATIONS.MUTE_LINK.y-30, HUD_LOCATIONS.MUTE_LINK.y+10);
  }

  getScaledClickLocation(clickPoint) {
    return {
      x: (clickPoint.x - this.position.x) / this.scale.x,
      y: (clickPoint.y - this.position.y) / this.scale.y
    };
  }
  /**
   * flyAway
   * Helper method that causes the sky to change color and the ducks to fly away
   * @returns {Promise} - This promise is resolved when all the ducks have flown away
   */
  skyColor(color) {
    // spans the letterbox bars too, so the pink fly-away flash fills the panel
    this.sky.clear().rect(-MAX_X, 0, MAX_X * 3, MAX_Y).fill(color);
  }

  flyAway() {
    this.dog.stopAndClearTimeline();
    this.dog.laugh();
    this.lock();
    const duckPromises = [];

    for (let i = 0; i < this.ducks.length; i++) {
      const duck = this.ducks[i];
      if (duck.alive) {
        duckPromises.push(new BPromise((resolve) => {
          duck.stopAndClearTimeline();
          duck.flyTo({
            point: new Point(MAX_X / 2, -500),
            onComplete: resolve
          });
        }));
      }
    }

    return BPromise.all(duckPromises).then(this.cleanUpDucks.bind(this)).then(this.unlock.bind(this));
  }

  /**
   * cleanUpDucks
   * Helper that removes all ducks from the container and object
   */
  cleanUpDucks() {
    for (let i = 0; i < this.ducks.length; i++) {
      this.removeChild(this.ducks[i]);
    }
    this.ducks = [];
  }

  /**
   * ducksAlive
   * Helper that returns a boolean value depending on whether or not ducks are alive. The distinction
   * is that even dead ducks may be animating and still "active"
   * @returns {Boolean}
   */
  ducksAlive() {
    return _some(this.ducks, (duck) => {
      return duck.alive;
    });
  }

  /**
   * ducksActive
   * Helper that returns a boolean value depending on whether or not ducks are animating. Both live
   * and dead ducks may be animating.
   * @returns {Boolean}
   */
  ducksActive() {
    return _some(this.ducks, (duck) => {
      return duck.isActive();
    });
  }

  /**
   * dogActive
   * Helper proxy method that returns a boolean depending on whether the dog is animating
   * @returns {boolean}
   */
  dogActive() {
    return this.dog.isActive();
  }

  /**
   * isActive
   * High level helper to determine if things are animating on the stage
   * @returns {boolean|Boolean}
   */
  isActive() {
    return this.dogActive() || this.ducksAlive() || this.ducksActive();
  }

  /**
   * lock
   * Lock the stage to prevent new animations from being added to timelines, specifically useful for managing race and
   * edge conditions around dogs and ducks.
   */
  lock() {
    this.locked = true;
  }

  /**
   * unlock
   * Unlock the stage so that new animations can be added to timelines.
   */
  unlock() {
    this.locked = false;
  }

  /**
   * isLocked
   * Helper to tell if the stage is locked to new animations or not
   * @returns {Boolean}
   */
  isLocked() {
    return this.locked;
  }
}

export default Stage;
